import { useSelector } from '~/_shared/utils/hooks/useSelector';
import { createStateSelector } from '~/_shared/utils/memoize/createSelector';
import { isBoundaryTerritoryHidden } from '~/boundary/settings/boundarySettings.helpers';
import type { AppState } from '~/store/app.store';
import { checkBoundaryGroupPermissionsSelector } from '~/store/boundaryGroups/boundaryGroups.selectors';
import { displayNameComparer } from '~/store/boundaryItems/boundaryItems.helpers';
import { mapBoundariesSelector } from '~/store/boundaryItems/boundaryItems.selectors';
import type { BoundaryStateItem } from '~/store/boundaryItems/boundaryItems.state';
import { boundaryTerritoryAssignmentsSelector } from '~/store/boundaryTerritoryDetails/boundaryTerritoryDetails.selectors';
import type {
  BoundaryTerritory, BoundaryTerritoryGroup,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { isBoundaryTerritoryEmpty } from '../fill/boundaryFill.helpers';

const boundaryTerritoryListSelector = createStateSelector([
  (_state, boundaryTerritoryGroup: BoundaryTerritoryGroup) => boundaryTerritoryGroup,
  boundaryTerritoryAssignmentsSelector,
], (
  boundaryTerritoryGroup,
  boundaryTerritoryAssignments,
) => {
  if (!boundaryTerritoryGroup) {
    return [];
  }

  return boundaryTerritoryGroup.settings.boundaryTerritories.filter(boundaryTerritory => {
    const isEmpty = isBoundaryTerritoryEmpty(boundaryTerritoryAssignments,
      boundaryTerritoryGroup.boundaryTerritoryGroupId,
      boundaryTerritory);
    const isHidden = isBoundaryTerritoryHidden(boundaryTerritory,
      isEmpty,
      boundaryTerritoryGroup.settings.boundaryTerritoryType);

    return !isHidden;
  });
});

type SortedBoundaryListSelectorReturnType = readonly BoundaryStateItem[];

const sortedBoundaryListSelector: (state: AppState, boundaryTerritoryGroup?: BoundaryTerritoryGroup) => SortedBoundaryListSelectorReturnType = createStateSelector([
  (_state, boundaryTerritoryGroup: BoundaryTerritoryGroup) => boundaryTerritoryGroup,
  checkBoundaryGroupPermissionsSelector,
  mapBoundariesSelector,
], (
  boundaryTerritoryGroup,
  { checkBoundaryGroupPermissions },
  mapBoundaries
) => {
  const boundaryPermissions = checkBoundaryGroupPermissions(boundaryTerritoryGroup?.boundaryGroupId);
  const { boundaryIsCustom } = boundaryPermissions;
  const groupBoundaries = boundaryTerritoryGroup && mapBoundaries.get(boundaryTerritoryGroup.boundaryGroupId);

  if (!boundaryIsCustom) {
    // we can skip sorting when not custom boundaries, as they are not used and it's performance heavy
    return [];
  }
  return [...(groupBoundaries?.values() ?? [])].sort(displayNameComparer);
});

type BoundaryItemsSelectorReturnType = Readonly<{
  displayBoundaryTerritories: boolean;
  boundaryTerritoryList: readonly BoundaryTerritory[];
  sortedBoundaryList: ReturnType<typeof sortedBoundaryListSelector>;
  areGroupBoundariesEmpty: boolean;
  boundaryPermissions: ReturnType<ReturnType<typeof checkBoundaryGroupPermissionsSelector>['checkBoundaryGroupPermissions']>;
}>;

export const boundaryItemsSelector: (state: AppState, boundaryTerritoryGroup?: BoundaryTerritoryGroup) => BoundaryItemsSelectorReturnType = createStateSelector([
  (_state, boundaryTerritoryGroup?: BoundaryTerritoryGroup) => boundaryTerritoryGroup,
  boundaryTerritoryListSelector,
  checkBoundaryGroupPermissionsSelector,
  mapBoundariesSelector,
  sortedBoundaryListSelector,
], (
  boundaryTerritoryGroup,
  boundaryTerritoryList,
  { checkBoundaryGroupPermissions },
  mapBoundaries,
  sortedBoundaryList
) => {
  const boundaryPermissions = checkBoundaryGroupPermissions(boundaryTerritoryGroup?.boundaryGroupId);
  const { boundaryIsCustom } = boundaryPermissions;

  const displayBoundaryTerritories = !boundaryIsCustom || (!!boundaryTerritoryGroup && boundaryTerritoryGroup.settings.boundaryTerritories.length > 0);

  const groupBoundaries = boundaryTerritoryGroup && mapBoundaries.get(boundaryTerritoryGroup.boundaryGroupId);
  const areGroupBoundariesEmpty = !groupBoundaries || groupBoundaries.size === 0;

  return {
    displayBoundaryTerritories,
    boundaryTerritoryList,
    sortedBoundaryList,
    areGroupBoundariesEmpty,
    boundaryPermissions,
  };
});

export const useBoundaryItems = ({ boundaryTerritoryGroup }: {
  boundaryTerritoryGroup?: BoundaryTerritoryGroup;
}) => useSelector(state => boundaryItemsSelector(state, boundaryTerritoryGroup));
